import { useState, useEffect } from 'react';
import StatCard from "../../../components/common/StatCard";
import ComponentCard from "../../../components/common/ComponentCard";
import Button from "../../../components/ui/button/Button";
import { DollarSign, Clock, Plus, Download, BarChart, TrendingUp, Users } from 'lucide-react';
import useApi from "../../../hooks/useApi";
import FacturaList from "./FacturaList";
import { FacturaForm } from "./FacturaForm";
import type { Factura, FacturaFormValues } from "../types/index";
import { FormikHelpers } from 'formik';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { mapFacturaToFormValues } from '../types/index';
import { Modal } from "../../../components/ui/modal";
import useAuth from "../../../hooks/useAuth";
import RoleBasedRenderer from "../../../components/auth/RoleBasedRenderer";

// Definir la interfaz para las estadísticas
interface FacturaStats {
  totalFacturas: number;
  totalIngresos: number;
  pendientesCobro: number;
  clientesActivos: number;
  porEstado: {
    pendientes: number;
    pagadas: number;
    anuladas: number;
    abonadas: number;
  };
  tendencias: {
    ingresos: number;
    pendientes: number;
    clientes: number;
    facturas: number;
  };
  desglose?: {
    manuales: number;
    automaticas: number;
  };
}

// Update the adapter function
const adaptFactura = (apiFactura: any): Factura => ({
  id: apiFactura.id,
  numero: apiFactura.numero,
  fecha: apiFactura.fecha,
  clientId: apiFactura.clienteId || apiFactura.clientId,
  clienteNombre: apiFactura.clienteNombre,
  estado: apiFactura.estado,
  total: apiFactura.total,
  subtotal: apiFactura.subtotal,
  impuestoTotal: apiFactura.impuestoTotal || apiFactura.impuestos,
  lineas: apiFactura.lineas || []
});

export default function FacturasView() {
  const { 
    getFacturas, 
    getFacturaStats, 
    exportFacturas, 
    generateFacturaReport,
    createFactura,
    updateFactura,
    formatCurrency 
  } = useApi();
  
  const { canCreate } = useAuth();
  
  const [facturas, setFacturas] = useState<Factura[]>([]);
  const [selectedFactura, setSelectedFactura] = useState<Factura | null>(null);
  const [stats, setStats] = useState<FacturaStats>({
    totalFacturas: 0,
    totalIngresos: 0,
    pendientesCobro: 0,
    clientesActivos: 0,
    porEstado: {
      pendientes: 0,
      pagadas: 0,
      anuladas: 0,
      abonadas: 0
    },
    tendencias: {
      ingresos: 0,
      pendientes: 0,
      clientes: 0,
      facturas: 0
    },
    desglose: {
      manuales: 0,
      automaticas: 0
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    startDate: format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    estado: 'todas' as 'pendiente' | 'pagada' | 'anulada' | 'todas'
  });

  // Función dedicada para actualizar las estadísticas
  const refreshStats = async () => {
    try {
      const statsData = await getFacturaStats();
      if (statsData) {
        // Type assertion to match our interface
        setStats(statsData as FacturaStats);
      }
    } catch (error) {
      toast.error('Error al cargar las estadísticas');
    }
  };

  // Función para actualizar tanto las facturas como las estadísticas
  const refreshData = async () => {
    try {
      setIsLoading(true);
      
      // Actualizar estadísticas y facturas en paralelo
      await Promise.all([
        refreshStats(),
        getFacturas().then(facturasData => {
          setFacturas(facturasData.map(adaptFactura));
        })
      ]);
      
    } catch (error) {
      toast.error('Error al actualizar los datos');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshData();
  }, []);

  const handleExportData = async () => {
    try {
      toast.loading('Generando archivo de Excel...', { id: 'exportToast' });
      
      // Verificar si hay facturas para exportar
      if (facturas.length === 0) {
        toast.error('No hay facturas disponibles para exportar.', { id: 'exportToast' });
        return;
      }
      
      // Intentar usar el endpoint de backend para generar Excel
      try {
        const blob = await exportFacturas(exportFilters);
        toast.success('Excel generado correctamente', { id: 'exportToast' });
        
        // Crear un enlace para descargar el archivo
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `facturas_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
        document.body.appendChild(a);
        a.click();
        
        // Limpieza
        setTimeout(() => {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }, 100);
      } catch (exportError: any) {
        
        // Si hay un error con el endpoint, generar un CSV básico localmente
        generateLocalCSV();
      }
      
      // Cerrar el modal de exportación
      setIsExportModalOpen(false);
    } catch (error: any) {
      toast.error('Error inesperado al exportar datos. Intente nuevamente.', { id: 'exportToast' });
    }
  };
  
  // Función para generar un CSV básico localmente
  const generateLocalCSV = () => {
    try {
      // Filtrar las facturas según los criterios seleccionados
      let filteredFacturas = [...facturas];
      
      // Aplicar filtro de fecha de inicio
      if (exportFilters.startDate) {
        const startDate = new Date(exportFilters.startDate);
        filteredFacturas = filteredFacturas.filter(f => {
          const facturaDate = new Date(f.fecha);
          return facturaDate >= startDate;
        });
      }
      
      // Aplicar filtro de fecha de fin
      if (exportFilters.endDate) {
        const endDate = new Date(exportFilters.endDate);
        endDate.setHours(23, 59, 59, 999); // Final del día
        filteredFacturas = filteredFacturas.filter(f => {
          const facturaDate = new Date(f.fecha);
          return facturaDate <= endDate;
        });
      }
      
      // Aplicar filtro de estado
      if (exportFilters.estado !== 'todas') {
        filteredFacturas = filteredFacturas.filter(f => f.estado === exportFilters.estado);
      }
      
      // Generar contenido CSV
      const csvContent = [
        // Encabezados CSV
        ['ID', 'Número', 'Fecha', 'Cliente', 'Estado', 'Subtotal', 'Impuestos', 'Total', 'Notas'].join(','),
        // Filas de datos
        ...filteredFacturas.map(f => [
          f.id,
          f.numero,
          f.fecha,
          f.clienteNombre || 'N/A',
          f.estado,
          f.subtotal,
          f.impuestoTotal,
          f.total,
          (f.notas || '').replace(/,/g, ' ') // Eliminar comas en las notas para no afectar el formato CSV
        ].join(','))
      ].join('\n');
      
      // Crear blob CSV y descargar
      const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const csvUrl = URL.createObjectURL(csvBlob);
      const a = document.createElement('a');
      a.href = csvUrl;
      a.download = `facturas_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(a);
      a.click();
      
      // Limpieza
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(csvUrl);
      }, 100);
      
      toast.success('Se ha generado un archivo CSV con los datos de facturas.', { id: 'exportToast' });
    } catch (error) {
      toast.error('Error al generar el archivo CSV.', { id: 'exportToast' });
    }
  };

  const handleOpenExportModal = () => {
    setIsExportModalOpen(true);
  };

  const handleGenerateReport = async (id: number) => {
    try {
      const blob = await generateFacturaReport(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `factura-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Error al generar el reporte de factura');
    }
  };

  const handleSubmitFactura = async () => {
    try {
      // Recargar tanto la lista de facturas como las estadísticas
      await refreshData();
      setIsFormModalOpen(false);
    } catch (error) {
      toast.error('Error al actualizar los datos');
    }
  };

  const handleSubmit = async (values: FacturaFormValues, helpers: FormikHelpers<FacturaFormValues>) => {
    try {
      if (!values.clientId) {
        toast.error('El cliente es requerido');
        return;
      }

      const invoiceData = {
        numero: values.numero,
        fecha: values.fecha,
        clientId: values.clientId,
        estado: values.estado === 'abonada' ? 'pendiente' : values.estado as 'pendiente' | 'pagada' | 'anulada',
        subtotal: values.subtotal,
        impuestoTotal: values.impuestos,
        total: values.total,
        notas: values.notas,
        lineas: values.items.map(item => ({
          descripcion: item.descripcion,
          cantidad: item.cantidad,
          precioUnitario: item.precioUnitario,
          impuesto: item.impuesto,
          subtotal: item.subtotal,
          total: item.total,
          hotel: item.hotel,
          fechaServicio: item.fechaServicio,
          tipoServicio: item.tipoServicio,
          destino: item.destino,
          costoProveedor: item.costoProveedor,
          comision: item.comision
        })),
        entregadoPor: values.entregadoPor,
        recibidoPor: values.recibidoPor
      };

      if (selectedFactura?.id) {
        await updateFactura(selectedFactura.id, invoiceData);
        toast.success('Factura actualizada exitosamente');
      } else {
        await createFactura(invoiceData);
        toast.success('Factura creada exitosamente');
      }

      // Actualizar tanto facturas como estadísticas
      await handleSubmitFactura();
      setSelectedFactura(null);
    } catch (error) {
      toast.error('Error al procesar la factura');
      helpers.setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="px-6 py-5">
            <RoleBasedRenderer requiredRoles={['admin', 'supervisor']}>
              <StatCard
                title="Ingresos totales"
                value={isLoading ? '---' : formatCurrency(stats.totalIngresos)}
                trend={!isLoading && stats.tendencias ? stats.tendencias.ingresos : 0}
                icon={<DollarSign className="w-6 h-6" />}
                loading={isLoading}
              />
            </RoleBasedRenderer>
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="px-6 py-5">
            <StatCard
              title="Facturas emitidas"
              value={isLoading ? '---' : stats.totalFacturas.toString()}
              trend={!isLoading && stats.tendencias ? stats.tendencias.facturas : 0}
              icon={<TrendingUp className="w-6 h-6" />}
              loading={isLoading}
            />
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="px-6 py-5">
            <RoleBasedRenderer requiredRoles={['admin', 'supervisor']}>
              <StatCard
                title="Pagos pendientes"
                value={isLoading ? '---' : formatCurrency(stats.pendientesCobro)}
                trend={!isLoading && stats.tendencias ? stats.tendencias.pendientes : 0}
                icon={<Clock className="w-6 h-6" />}
                loading={isLoading}
              />
            </RoleBasedRenderer>
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="px-6 py-5">
            <StatCard
              title="Clientes y agencias activos"
              value={isLoading ? '---' : stats.clientesActivos.toString()}
              trend={!isLoading && stats.tendencias ? stats.tendencias.clientes : 0}
              icon={<Users className="w-6 h-6" />}
              loading={isLoading}
            />
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        <div className="xl:col-span-3">
          <FacturaList 
            facturas={facturas}
            onSearch={setSearchTerm}
            onDataChange={refreshData}
          />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <ComponentCard title="Acciones rápidas">
            <div className="space-y-3 p-4">
              <RoleBasedRenderer requiredPermission="create">
                <Button
                  variant="primary"
                  size="sm"
                  className="w-full justify-start"
                  startIcon={<Plus className="w-4 h-4" />}
                  onClick={() => setIsFormModalOpen(true)}
                >
                  Crear factura
                </Button>
              </RoleBasedRenderer>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                startIcon={<Download className="w-4 h-4" />}
                onClick={handleOpenExportModal}
              >
                Exportar datos
              </Button>
            </div>
          </ComponentCard>

          <ComponentCard title="Resumen de estado">
            <div className="grid grid-cols-2 divide-x divide-gray-200 dark:divide-gray-700">
              <div className="space-y-3 px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                  <div>
                    <span className="text-lg font-semibold text-gray-900 dark:text-white">
                      {isLoading ? '---' : stats.porEstado.pendientes}
                    </span>
                    <span className="ml-1 text-sm text-gray-500 dark:text-gray-400">pendientes</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <div>
                    <span className="text-lg font-semibold text-gray-900 dark:text-white">
                      {isLoading ? '---' : stats.porEstado.pagadas}
                    </span>
                    <span className="ml-1 text-sm text-gray-500 dark:text-gray-400">pagadas</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3 px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                  <div>
                    <span className="text-lg font-semibold text-gray-900 dark:text-white">
                      {isLoading ? '---' : stats.porEstado.anuladas}
                    </span>
                    <span className="ml-1 text-sm text-gray-500 dark:text-gray-400">anuladas</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                  <div>
                    <span className="text-lg font-semibold text-gray-900 dark:text-white">
                      {isLoading ? '---' : stats.porEstado.abonadas}
                    </span>
                    <span className="ml-1 text-sm text-gray-500 dark:text-gray-400">abonadas</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Add invoice type breakdown */}
            <div className="mt-4 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                  <div>
                    <span className="text-lg font-semibold text-gray-900 dark:text-white">
                      {isLoading ? '---' : stats.desglose?.manuales || 0}
                    </span>
                    <span className="ml-1 text-sm text-gray-500 dark:text-gray-400">manuales</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-indigo-400 rounded-full"></div>
                  <div>
                    <span className="text-lg font-semibold text-gray-900 dark:text-white">
                      {isLoading ? '---' : stats.desglose?.automaticas || 0}
                    </span>
                    <span className="ml-1 text-sm text-gray-500 dark:text-gray-400">automáticas</span>
                  </div>
                </div>
              </div>
            </div>
          </ComponentCard>
        </div>
      </div>

      {/* Renderiza el modal de FacturaForm */}
      {isFormModalOpen && (
        <FacturaForm
          isOpen={isFormModalOpen}
          onClose={() => {
            setIsFormModalOpen(false);
            setSelectedFactura(null);
          }}
          onCancel={() => {
            setIsFormModalOpen(false);
            setSelectedFactura(null);
          }}
          onSuccess={handleSubmitFactura}
          onSubmit={handleSubmit}
          factura={selectedFactura ? mapFacturaToFormValues(selectedFactura) : undefined}
        />
      )}

      {/* Modal de exportación */}
      {isExportModalOpen && (
        <Modal
          isOpen={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          title="Exportar facturas a Excel"
        >
          <div className="space-y-4 pb-2">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Seleccione el período y el estado de las facturas a exportar
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Fecha inicio
                </label>
                <input
                  type="date"
                  id="startDate"
                  value={exportFilters.startDate}
                  onChange={(e) => setExportFilters({...exportFilters, startDate: e.target.value})}
                  className="block w-full shadow-sm sm:text-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Fecha fin
                </label>
                <input
                  type="date"
                  id="endDate"
                  value={exportFilters.endDate}
                  onChange={(e) => setExportFilters({...exportFilters, endDate: e.target.value})}
                  className="block w-full shadow-sm sm:text-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="estado" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Estado de facturas
              </label>
              <select
                id="estado"
                value={exportFilters.estado}
                onChange={(e) => setExportFilters({...exportFilters, estado: e.target.value as any})}
                className="block w-full shadow-sm sm:text-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              >
                <option value="todas">Todas</option>
                <option value="pendiente">Pendientes</option>
                <option value="pagada">Pagadas</option>
                <option value="anulada">Anuladas</option>
              </select>
            </div>
            
            <div className="pt-4 flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setIsExportModalOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleExportData}
              >
                Exportar
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}